import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

// Inline the compiled CSS (~29KB raw, ~6KB gzip) directly into the document
// head as a <style> tag instead of a render-blocking <link rel="stylesheet">.
// This removes the CSS request from the critical path entirely - first paint no
// longer waits on a CSS round-trip, and the font dependency chain shortens from
// HTML->CSS->font to HTML->font (fonts are preloaded below).
import appCss from "../styles.css?inline";
import favicon32 from "../assets/favicon-32.png";
import favicon192 from "../assets/favicon-192.png";
import soraSemibold from "@fontsource/sora/files/sora-latin-600-normal.woff2";
import manropeRegular from "@fontsource/manrope/files/manrope-latin-400-normal.woff2";
import manropeSemibold from "@fontsource/manrope/files/manrope-latin-600-normal.woff2";
import { SITE } from "../lib/site-config";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteLayout } from "@/components/SiteLayout";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // Per-page title/description/canonical/OG/robots are set by buildHead().
      // Only a brand-level <title> fallback lives here (no canonical in __root).
      { title: `${SITE.name} - Business Loans in Dallas, TX` },
    ],
    links: [
      // The stylesheet is inlined as a <style> tag in RootShell (see appCss
      // import) - there is no render-blocking stylesheet <link> here.
      // Font preloads are injected BEFORE HeadContent in RootShell so they are
      // discovered ahead of the inline styles.
      { rel: "icon", type: "image/png", href: favicon32 },
      { rel: "apple-touch-icon", href: favicon192 },
      { rel: "preconnect", href: "https://images.unsplash.com", crossOrigin: "anonymous" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        {/* Font preloads MUST appear before the inline styles so the browser
            discovers them before parsing CSS - putting them here guarantees that
            order. */}
        <link rel="preload" as="font" type="font/woff2" href={soraSemibold} crossOrigin="anonymous" />
        <link rel="preload" as="font" type="font/woff2" href={manropeRegular} crossOrigin="anonymous" />
        <link rel="preload" as="font" type="font/woff2" href={manropeSemibold} crossOrigin="anonymous" />
        {/* Inlined compiled CSS - no render-blocking stylesheet request. */}
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return <SiteLayout />;
}
